import React, { useRef, useState, Fragment, useEffect } from "react"
import { faHandshake } from "@fortawesome/pro-solid-svg-icons/faHandshake"
import { faBullhorn } from "@fortawesome/pro-solid-svg-icons/faBullhorn"
import { faUsers } from "@fortawesome/pro-solid-svg-icons/faUsers"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useIntl, FormattedMessage } from "gatsby-plugin-intl"
import { faCopy } from "@fortawesome/pro-light-svg-icons/faCopy"
import { getAllScreenTypes } from "@tmu/utils/mediaQueries"
import SEO from "@tmu/components/seo"
import { useLazyQuery } from "@apollo/client"
import { ZAPIER_QUERY } from "@tmu/apollo/storefront/queries/user"
import theme from "@tmu/global/theme"

import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons/faCircleInfo"
import { faCheck } from "@fortawesome/pro-regular-svg-icons/faCheck"
import {
  StyledAboutUsWrapper,
  StyledAboutUsHero,
  StyledAboutUsHeroText,
  StyledAboutUsHeroSubText,
  StyledFeatureTagsContainer,
  StyledFeatureTag,
  StyledZapierIntegrationSection,
  StyledZapierPuzzleImage,
  StyledZapierIntegrationContent,
  StyledZapierIntegrationTitle,
  StyledZapierInfoBox,
  StyledBlurEdge,
  StyledCodeBox,
  StyledCodeLabel,
  StyledButtonRow,
  StyledPrimaryButton,
  StyledSecondaryButton,
} from "./index.styles"
import zapierPuzzleImage from "../../assets/images/zapier.png"

const featureTags = [
  { id: "zapier::tag::crm", defaultMessage: "SYNC DONATIONS WITH CRM" },
  {
    id: "zapier::tag::thankyou",
    defaultMessage: "AUTOMATED DONATION THANK-YOU EMAILS",
  },
  { id: "zapier::tag::events", defaultMessage: "MANAGE EVENT REGISTRATIONS" },
  {
    id: "zapier::tag::segmentation",
    defaultMessage: "DONOR SEGMENTATION IN EMAIL MARKETING",
  },
  {
    id: "zapier::tag::notifications",
    defaultMessage: "REAL-TIME TEAM NOTIFICATIONS",
  },
  {
    id: "zapier::tag::goals",
    defaultMessage: "TRACK GOALS WITH ANALYTICS TOOLS",
  },
  {
    id: "zapier::tag::engagement",
    defaultMessage: "MONITOR CAMPAIGN ENGAGEMENT",
  },
  { id: "zapier::tag::sms", defaultMessage: "SEND SMS NOTIFICATIONS" },
  {
    id: "zapier::tag::reports",
    defaultMessage: "GENERATE PERSONALIZED DONOR REPORTS",
  },
  {
    id: "zapier::tag::calendar",
    defaultMessage: "ADD CAMPAIGN DATES TO GOOGLE CALENDAR",
  },
  { id: "zapier::tag::orders", defaultMessage: "TRACK YOUR ORDERS" },
  {
    id: "zapier::tag::bi",
    defaultMessage: "VISUALIZE DONATION DATA IN BI TOOLS",
  },
]

const ZapierPage = () => {
  const { locale, formatMessage } = useIntl()
  const ref = useRef()
  const { isTablet, isDesktop, isWide } = getAllScreenTypes()
  const [code, setCode] = useState("")
  const [copied, setCopied] = useState(false)

  const [getZapier, { loading, data, refetch }] = useLazyQuery(ZAPIER_QUERY, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (!data || !data?.integrationToken) {
        setTimeout(() => refetch(), 10000)
      }
    },
  })
  useEffect(() => {
    getZapier()
  }, [])

  useEffect(() => {
    if (data?.integrationToken) {
      setCode(data.integrationToken.token)
    }
  }, [data])

  console.log("data", data)

  const handleCopy = () => {
    navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  const handleGenerate = () => {
    getZapier()
    setCopied(false)
  }

  return (
    <>
      <SEO
        lang={locale}
        title={formatMessage({
          id: "zapier::integration::pageTitle",
          defaultMessage: "Zapier Integration",
        })}
        description={formatMessage({
          id: "zapier::integration::headline",
          defaultMessage:
            "Enhance your TrustMeUp experience — with no developers, no IT tickets, no delays",
        })}
      />
      <StyledAboutUsWrapper ref={ref}>
        <StyledAboutUsHero>
          <StyledAboutUsHeroText>
            <FormattedMessage
              id="zapier::integration::headline1"
              defaultMessage={"Enhance your TrustMeUp experience —"}
            />
            <br />
            <FormattedMessage
              id="zapier::integration::headline2"
              defaultMessage={"with no developers, no IT tickets, no delays"}
            />
          </StyledAboutUsHeroText>
          <StyledAboutUsHeroSubText>
            <FormattedMessage
              id="zapier::integration::subtext"
              defaultMessage="Learn more about how to automate your workflows with Zapier integration on <strong></strong>"
              values={{
                strong: (...chunks) => (
                  <a
                    href="https://zapier.com/apps/trustmeup/integrations"
                    target="_blank"
                    rel="noopener noreferrer">
                    <FormattedMessage
                      id="zapier::integration::officialWebsite"
                      defaultMessage="the official website"
                    />
                  </a>
                ),
              }}
            />
          </StyledAboutUsHeroSubText>
        </StyledAboutUsHero>

        {/* Feature tags section with blur edges and 3 rows */}
        <div
          style={{
            position: "relative",
            marginBottom: "2.5rem",
            display: "flex",
            justifyContent: "center",
          }}>
          <StyledBlurEdge left />
          <StyledFeatureTagsContainer>
            {featureTags.map((tag, i) => (
              <StyledFeatureTag key={tag.id} row={Math.floor(i / 4)}>
                {formatMessage({
                  id: tag.id,
                  defaultMessage: tag.defaultMessage,
                })}
              </StyledFeatureTag>
            ))}
          </StyledFeatureTagsContainer>
          <StyledBlurEdge right />
        </div>

        {/* Zapier integration code section */}
        <StyledZapierIntegrationSection>
          <div>
            <StyledZapierPuzzleImage
              src={zapierPuzzleImage}
              alt="TrustMeUp Zapier"
            />
            <StyledZapierIntegrationContent>
              <StyledZapierIntegrationTitle>
                <FormattedMessage
                  id="zapier::integration::codeTitle"
                  defaultMessage="Zapier integration code"
                />
              </StyledZapierIntegrationTitle>
              <div style={{ fontSize: "1rem", marginBottom: "1.25rem" }}>
                <FormattedMessage
                  id="zapier::integration::instructions"
                  defaultMessage="Generate Zapier integration code, proceed to Zapier integration page and paste it in the code field."
                />
              </div>
              <StyledZapierInfoBox>
                <FontAwesomeIcon
                  className="icon info-icon"
                  icon={faCircleInfo}
                />
                <FormattedMessage
                  id="zapier::integration::infoBox"
                  defaultMessage="To integrate, use the same email for both your Zapier and TrustMeUp accounts."
                />
              </StyledZapierInfoBox>
              <StyledCodeLabel className="caption">
                <FormattedMessage
                  id="zapier::integration::yourCodeLabel"
                  defaultMessage="YOUR CODE"
                />
                :
              </StyledCodeLabel>
              <StyledCodeBox as="textarea" rows={3} value={code} readOnly />
              <StyledButtonRow>
                {!code ? (
                  <StyledPrimaryButton onClick={handleGenerate}>
                    <FormattedMessage
                      id="zapier::integration::generateZapierCodeButton"
                      defaultMessage="Generate zapier code"
                    />
                  </StyledPrimaryButton>
                ) : (
                  <>
                    {/* <StyledPrimaryButton onClick={handleGenerate}>
                      <FormattedMessage
                        id="zapier::integration::generateButton"
                        defaultMessage="GENERATE NEW CODE"
                      />
                    </StyledPrimaryButton> */}

                    <StyledSecondaryButton onClick={handleCopy}>
                      <FormattedMessage
                        id="zapier::integration::copyButton"
                        defaultMessage="COPY CODE"
                      />
                      <span style={{ marginLeft: 8 }}>
                        {copied ? (
                          <FontAwesomeIcon icon={faCheck} />
                        ) : (
                          <FontAwesomeIcon icon={faCopy} />
                        )}
                      </span>
                    </StyledSecondaryButton>
                  </>
                )}
              </StyledButtonRow>
              <div style={{ fontSize: "1rem", marginTop: "2.25rem" }}>
                <FormattedMessage
                  id="zapier::integration::storeIdText1"
                  defaultMessage="If you have a store account on TrustMeUp, you will need to enter your Store ID during the Zapier integration process."
                />
              </div>
              <div style={{ fontSize: "1rem", marginTop: "0.75rem" }}>
                <FormattedMessage
                  id="zapier::integration::storeIdText2"
                  defaultMessage="You can find your Store ID on your TrustMeUp account's <a>Account Information page</a>."
                  values={{
                    a: (...chunks) => (
                      <a
                        style={{ color: theme?.colors?.blue }}
                        href={`/${locale}/dashboard/merchants/account/`}
                        target="_blank">
                        {chunks}
                      </a>
                    ),
                  }}
                />
              </div>
            </StyledZapierIntegrationContent>
          </div>
        </StyledZapierIntegrationSection>
      </StyledAboutUsWrapper>
    </>
  )
}

export default ZapierPage
